import { type MutableRefObject, memo, type ReactNode, useCallback } from 'react';

import { useScrollElementContext } from '~/components/v2/components/shared/scroll/ScrollElementContextProvider';

export type ItemPickerScrollAreaProps = {
  children: ReactNode;
  /**
   * Exposes this scroll element to the owner, which passes it to useItemPickerMarqueeSelection for
   * edge auto-scroll.
   */
  scrollAreaRef: MutableRefObject<HTMLDivElement | null>;
};

/**
 * Registers the scrollable content column with the ScrollElementContext so the virtualized asset
 * grid can measure against it. Must be rendered inside a ScrollElementContextProvider.
 *
 * The element is exposed both into ScrollElementContext (for the virtualized grid) and out through
 * scrollAreaRef (for the picker's marquee auto-scroll).
 */
export const ItemPickerScrollArea = memo(({ children, scrollAreaRef }: ItemPickerScrollAreaProps) => {
  const { setScrollElement } = useScrollElementContext();

  const setScrollRefs = useCallback(
    (node: HTMLDivElement | null) => {
      scrollAreaRef.current = node;
      setScrollElement(node);
    },
    [scrollAreaRef, setScrollElement],
  );

  return (
    <div className="flex-1 overflow-y-auto pb-4" data-testid="ITEM_PICKER_SCROLL_AREA" ref={setScrollRefs}>
      <div className="px-6 pt-4">{children}</div>
    </div>
  );
});

ItemPickerScrollArea.displayName = 'ItemPickerScrollArea';
